import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useSiteContent} from '../../hooks/useSiteContent'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiPlus, FiMinus} = FiIcons

const FAQSection = ({content = {}}) => {
  const [openIndex, setOpenIndex] = useState(null)
  const {content: siteContent} = useSiteContent()

  const {
    title = "Frequently Asked Questions",
    questions = []
  } = content

  const defaultQuestions = [
    {
      question: "How is this different from other diet programs?",
      answer: `We focus on sustainable habits, not quick fixes. Our ${siteContent?.howitworks_ppc_definition || 'PPC method (Protein, Produce, and Carbs)'} teaches you principles you can use for life, not restrictive rules that fail when life gets busy.`
    },
    {
      question: "Do I need to count calories or track macros?",
      answer: "Not necessarily. We teach portion awareness and intuitive eating principles. Some people find tracking helpful initially, but our goal is to help you develop natural awareness."
    },
    {
      question: "How quickly will I see results?",
      answer: "Most people see initial changes within 2-3 weeks, but sustainable weight loss happens at 1-2 lbs per week. We focus on building habits that last, not quick drops that rebound."
    },
    {
      question: "What if I have dietary restrictions or medical conditions?",
      answer: "Our approach works with most dietary needs. We always recommend consulting with your healthcare provider, and our methods can be adapted for various restrictions and conditions."
    },
    {
      question: "Is there ongoing support after I complete a course?",
      answer: "Yes! Course graduates get access to our private community, monthly Q&A calls, and ongoing resources to help maintain your progress long-term."
    }
  ]

  const questionsToShow = questions.length > 0 ? questions : defaultQuestions

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about our approach to sustainable weight loss
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {questionsToShow.map((faq, index) => (
            <motion.div
              key={index}
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.1}}
              viewport={{once: true}}
              className="bg-white rounded-xl border border-gray-200 hover:border-nutrition-300 transition-colors"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between focus:outline-none"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0 w-8 h-8 bg-nutrition-100 rounded-full flex items-center justify-center">
                  <SafeIcon 
                    icon={openIndex === index ? FiMinus : FiPlus} 
                    className="w-4 h-4 text-nutrition-600" 
                  />
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{height: 0, opacity: 0}}
                    animate={{height: 'auto', opacity: 1}}
                    exit={{height: 0, opacity: 0}}
                    transition={{duration: 0.3}}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.3}}
          viewport={{once: true}}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Book a consultation call to discuss your specific situation and goals.
            </p>
            <a
              href={siteContent?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation'}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
            >
              <span>{siteContent?.cta_secondary_label || 'Book a Call'}</span>
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default FAQSection